"use client";

import { DetailSkeleton } from "@/components/ui/skeleton";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport, type FileUIPart } from "ai";
import { supabase } from "@/lib/supabase";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import { Spinner } from "@/components/ui/spinner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { ChevronDown } from "lucide-react";
import { SendIcon } from "@/components/ui/send";
import { toast } from "sonner";
import { MessageResponse } from "@/components/ai-elements/message";

const SUPABASE_URL = process.env.NEXT_PUBLIC_SUPABASE_URL ?? "";
const SUPABASE_KEY = process.env.NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY ?? "";

// ─── Types ────────────────────────────────────────────────────────────────────

type UserSummary = {
  user_id: string;
  display_name: string | null;
  username: string | null;
  role: string | null;
  email: string | null;
};

type EmailDraft = {
  subject: string;
  heading: string;
  subheading?: string;
  bodyHtml: string;
  buttons?: { label: string; url: string }[];
  includeImages?: boolean;
};

type MailCommsSend = {
  id: string;
  subject: string;
  heading: string;
  recipient_user_ids: string[];
  sent_count: number;
  failed_count: number;
  created_at: string;
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getDisplayName = (user: Pick<UserSummary, "display_name" | "username" | "email">) =>
  user.display_name?.trim() || user.username?.trim() || user.email?.split("@")[0] || "Unknown";

const getInitials = (name: string) =>
  name.trim().split(/\s+/).filter(Boolean).map((p) => p[0]?.toUpperCase() ?? "").join("").slice(0, 2) || "?";

// ─── Client-side email preview builder (mirrors _shared/email.ts) ─────────────

function escHtml(text: string) {
  return text
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

function injectImages(html: string, imageUrls: string[]): string {
  return html.replace(/\{\{image_(\d+)\}\}/g, (_, i) => {
    const url = imageUrls[Number(i)];
    if (!url) return "";
    return `<p style="margin:0 0 16px 0;"><img src="${url}" alt="Attached image" style="max-width:100%;height:auto;border-radius:4px;display:block;" /></p>`;
  });
}

function buildEmailPreviewHtml(draft: EmailDraft, imageUrls: string[] = []): string {
  const { subject: _subject, heading, subheading, buttons = [] } = draft;
  const bodyHtml = injectImages(draft.bodyHtml, imageUrls);

  const buttonsHtml = buttons.length > 0
    ? `<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin-top:4px;"><tr>
        ${buttons.map((btn) => `<td style="padding-right:12px;padding-bottom:8px;">
          <a href="${escHtml(btn.url)}" style="display:inline-block;padding:14px 24px;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:13px;line-height:18px;font-weight:bold;letter-spacing:0.7px;text-transform:uppercase;color:#ffffff;text-decoration:none;border-radius:2px;background-color:#AB2328;">
            ${escHtml(btn.label)}
          </a>
        </td>`).join("")}
      </tr></table>`
    : "";

  return `<!DOCTYPE html><html lang="en-NZ"><head><meta charset="utf-8"/>
<style>body{margin:0;padding:0;background-color:#f5f5f2;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;}.email-body,.email-body p{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif!important;font-weight:300!important;}</style>
</head><body>
<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color:#f5f5f2;">
  <tr><td align="center" style="padding:40px 16px;">
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="620" style="width:620px;max-width:100%;background-color:#ffffff;border:1px solid #e5e5e3;">
      <tr><td style="padding:0;"><table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%"><tr><td width="68%" style="height:2px;line-height:2px;font-size:2px;background-color:#d0d0cd;">&nbsp;</td><td width="32%" style="height:9px;line-height:9px;font-size:9px;background-color:#AB2328;">&nbsp;</td></tr></table></td></tr>
      <tr><td style="padding:44px 48px 48px 48px;">
        <h1 style="margin:0 0 8px 0;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:30px;line-height:34px;font-weight:600;color:#A1A1A1;letter-spacing:-0.4px;text-transform:uppercase;">${escHtml(heading)}</h1>
        ${subheading ? `<h2 style="margin:0 0 32px 0;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:16px;line-height:22px;font-weight:600;color:#414042;text-transform:uppercase;">${escHtml(subheading)}</h2>` : ""}
        <div class="email-body" style="font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-weight:300;">${bodyHtml}</div>
        ${buttonsHtml}
      </td></tr>
      <tr><td style="padding:22px 48px 26px 48px;border-top:1px solid #e5e5e3;background-color:#fafaf8;">
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%"><tr>
          <td valign="middle"><table role="presentation" cellpadding="0" cellspacing="0" border="0">
            <tr>
              <td valign="middle"><picture><source srcset="https://ai.woods.co.nz/woods-icon.svg" type="image/svg+xml" /><img src="https://ai.woods.co.nz/woods-icon-email.png" width="30" height="30" alt="Woods" style="display:block;width:30px;height:30px;" /></picture></td>
            </tr>
            <tr><td style="padding-top:9px;font-family:Arial,Helvetica,sans-serif;font-size:12px;line-height:17px;font-weight:700;letter-spacing:0.8px;color:#AB2328;text-transform:uppercase;white-space:nowrap;">Simply done well</td></tr>
          </table></td>
          <td align="right" valign="middle"><a href="https://ai.woods.co.nz" style="font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;line-height:18px;font-weight:600;color:#414042;text-decoration:none;letter-spacing:0.2px;">ai.woods.co.nz&nbsp;&nbsp;&rarr;</a></td>
        </tr></table>
      </td></tr>
    </table>
  </td></tr>
</table>
</body></html>`;
}

// ─── Recipients Picker ────────────────────────────────────────────────────────

function RecipientsPicker({
  users,
  loading,
  selectedIds,
  onChange,
}: {
  users: UserSummary[];
  loading: boolean;
  selectedIds: string[];
  onChange: (ids: string[]) => void;
}) {
  const [open, setOpen] = useState(false);

  const toggle = (id: string) =>
    onChange(selectedIds.includes(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id]);

  const label =
    selectedIds.length === 0
      ? "Select recipients…"
      : selectedIds.length === 1
      ? getDisplayName(users.find((u) => u.user_id === selectedIds[0]) ?? { display_name: null, username: null, email: null })
      : `${selectedIds.length} recipients selected`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
          disabled={loading}
        >
          <span className="truncate text-sm">{loading ? "Loading users…" : label}</span>
          <ChevronDown className="ml-2 size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <Command>
          <CommandInput placeholder="Search users…" />
          <CommandList>
            <CommandEmpty>No users found.</CommandEmpty>
            <CommandGroup>
              {users.map((user) => {
                const name = getDisplayName(user);
                const checked = selectedIds.includes(user.user_id);
                return (
                  <CommandItem
                    key={user.user_id}
                    value={`${name} ${user.email ?? ""}`}
                    onSelect={() => toggle(user.user_id)}
                    className="flex items-center gap-3 font-normal"
                  >
                    <Checkbox checked={checked} className="shrink-0" />
                    <Avatar className="size-6 shrink-0">
                      <AvatarFallback className="bg-foreground text-background text-xs">
                        {getInitials(name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-normal">{name}</p>
                      <p className="truncate text-xs text-muted-foreground">{user.email ?? "—"}</p>
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// ─── Chat Message ─────────────────────────────────────────────────────────────

function ChatMessage({ message }: { message: { role: string; parts: unknown[] } }) {
  const isUser = message.role === "user";
  const textParts = message.parts.filter(
    (p): p is { type: "text"; text: string } =>
      typeof p === "object" && p !== null && (p as { type: string }).type === "text"
  );
  const fileParts = message.parts.filter(
    (p): p is { type: "file"; url: string; mediaType: string; filename?: string } =>
      typeof p === "object" && p !== null && (p as { type: string }).type === "file"
  );

  if (textParts.length === 0 && fileParts.length === 0) return null;

  return (
    <div className={cn("flex flex-col gap-1", isUser ? "items-end" : "items-start")}>
      {/* Image thumbnails attached to this message */}
      {fileParts.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {fileParts.map((file, i) => (
            <div key={i} className="size-20 overflow-hidden rounded-lg border border-border bg-muted">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={file.url} alt={file.filename ?? "attachment"} className="size-full object-cover" />
            </div>
          ))}
        </div>
      )}
      {textParts.length > 0 && (
        <div
          className={cn(
            "max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm",
            isUser ? "bg-primary text-primary-foreground" : "bg-muted text-foreground"
          )}
        >
          {isUser ? (
            <p className="whitespace-pre-wrap">{textParts.map((p) => p.text).join("")}</p>
          ) : (
            <MessageResponse disallowedElements={["script", "style"]}>
              {textParts.map((p) => p.text).join("")}
            </MessageResponse>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Sent History ─────────────────────────────────────────────────────────────

function SentHistory({ refreshKey }: { refreshKey: number }) {
  const [rows, setRows] = useState<MailCommsSend[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    supabase
      .from("mail_comms_sends")
      .select("id, subject, heading, recipient_user_ids, sent_count, failed_count, created_at")
      .order("created_at", { ascending: false })
      .limit(50)
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) { console.error("[SentHistory]", error); }
        setRows((data as MailCommsSend[]) ?? []);
      });
    return () => { cancelled = true; };
  }, [refreshKey]);

  if (rows === null) {
    return (
      <DetailSkeleton />
    );
  }

  if (rows.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-2 p-8 text-center text-muted-foreground">
        <p className="text-sm font-medium">No emails sent yet</p>
        <p className="text-xs max-w-xs">Once you send a mail-comms batch, it will appear here.</p>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Date</TableHead>
            <TableHead>Subject</TableHead>
            <TableHead className="text-right">Recipients</TableHead>
            <TableHead className="text-right">Sent</TableHead>
            <TableHead className="text-right">Failed</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.id}>
              <TableCell className="text-muted-foreground text-xs">
                {new Date(row.created_at).toLocaleString()}
              </TableCell>
              <TableCell className="max-w-48 truncate font-medium">{row.subject}</TableCell>
              <TableCell className="text-right text-muted-foreground">{row.recipient_user_ids.length}</TableCell>
              <TableCell className="text-right text-foreground">{row.sent_count}</TableCell>
              <TableCell className={cn("text-right", row.failed_count > 0 ? "text-destructive" : "text-muted-foreground")}>
                {row.failed_count}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// ─── Email Preview (iframe renders the exact email HTML) ──────────────────────

function EmailPreview({ draft, imageUrls }: { draft: EmailDraft | null; imageUrls: string[] }) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [iframeHeight, setIframeHeight] = useState(600);
  const srcDoc = draft ? buildEmailPreviewHtml(draft, imageUrls) : null;

  useEffect(() => {
    if (!srcDoc) return;

    // Scroll preview to top whenever content changes
    scrollRef.current?.scrollTo({ top: 0 });

    const measure = () => {
      const doc = iframeRef.current?.contentDocument;
      if (!doc) return;
      const h = doc.documentElement.scrollHeight || doc.body?.scrollHeight || 0;
      if (h > 0) setIframeHeight(h + 24);
    };
    const iframe = iframeRef.current;
    if (!iframe) return;
    const onLoad = () => setTimeout(measure, 80);
    iframe.addEventListener("load", onLoad);
    setTimeout(measure, 120);
    return () => iframe.removeEventListener("load", onLoad);
  }, [srcDoc]);

  if (!draft || !srcDoc) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center text-muted-foreground">
        <SendIcon size={36} className="opacity-20" />
        <div>
          <p className="text-sm font-medium">No draft yet</p>
          <p className="mt-1 text-xs max-w-xs">
            Describe the email in the chat and the AI will draft and preview it here.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div ref={scrollRef} className="flex-1 overflow-y-auto">
      <div className="p-4">
        <iframe
          ref={iframeRef}
          srcDoc={srcDoc}
          title="Email preview"
          sandbox="allow-same-origin"
          className="w-full rounded border-0"
          style={{ height: `${iframeHeight}px`, minHeight: "400px", display: "block" }}
        />
      </div>
    </div>
  );
}

// ─── Main Panel ───────────────────────────────────────────────────────────────

export type MailCommsPanelProps = {
  onBack?: () => void;
};

export function MailCommsPanel({ onBack }: MailCommsPanelProps) {
  const [users, setUsers] = useState<UserSummary[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [input, setInput] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [historyKey, setHistoryKey] = useState(0);
  const [pendingFiles, setPendingFiles] = useState<FileUIPart[]>([]);
  const [sentImageUrls, setSentImageUrls] = useState<string[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const accessTokenRef = useRef<string | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      accessTokenRef.current = data.session?.access_token ?? null;
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_, s) => {
      accessTokenRef.current = s?.access_token ?? null;
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    let cancelled = false;
    supabase.auth.getSession().then(async ({ data }) => {
      const token = data.session?.access_token;
      if (!token) return;
      try {
        // get-users is cursor-paginated (see 20260901 Users panel rewrite) —
        // this picker needs everyone in one shot, so ask for a page large
        // enough to cover the whole org rather than just the first 25.
        const res = await fetch(`${SUPABASE_URL}/functions/v1/get-users`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, apikey: SUPABASE_KEY, "Content-Type": "application/json" },
          body: JSON.stringify({ pageSize: 10000 }),
        });
        const result = await res.json();
        if (!res.ok || result.error) throw new Error(result.error ?? "Request failed");
        if (!cancelled) setUsers(result.users ?? []);
      } catch (err) {
        console.error("[MailCommsPanel] failed to load users:", err);
        toast.error("Failed to load users list.");
      } finally {
        if (!cancelled) setUsersLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, []);

  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api: "/api/emails",
        headers: () => {
          const token = accessTokenRef.current;
          return token ? ({ Authorization: `Bearer ${token}` } as Record<string, string>) : ({} as Record<string, string>);
        },
      }),
    []
  );

  const { messages, sendMessage, status } = useChat({ transport });
  const isStreaming = status === "submitted" || status === "streaming";

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // All image URLs available for the email: already-sent + currently pending
  const conversationImageUrls = useMemo<string[]>(() => {
    const pendingImageUrls = pendingFiles
      .filter((f) => f.mediaType?.startsWith("image/"))
      .map((f) => f.url);
    return [...sentImageUrls, ...pendingImageUrls];
  }, [sentImageUrls, pendingFiles]);

  const emailDraft = useMemo<EmailDraft | null>(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const msg = messages[i];
      if (msg.role !== "assistant") continue;
      const parts = msg.parts as { type: string; state?: string; input?: unknown; output?: unknown }[];
      for (let j = parts.length - 1; j >= 0; j--) {
        const part = parts[j];
        if (
          part.type === "tool-setEmailDraft" &&
          (part.state === "output-available" || part.state === "result")
        ) {
          const data = (part.output ?? part.input) as EmailDraft | null;
          if (data?.subject && data?.heading && data?.bodyHtml) return data;
        }
      }
    }
    return null;
  }, [messages]);

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      const imageFiles = Array.from(e.clipboardData.files).filter((f) =>
        f.type.startsWith("image/")
      );
      if (imageFiles.length === 0) return;
      e.preventDefault();

      const readers = imageFiles.map(
        (file) =>
          new Promise<FileUIPart>((resolve) => {
            const reader = new FileReader();
            reader.onload = () =>
              resolve({
                type: "file",
                mediaType: file.type,
                filename: file.name || "pasted-image.png",
                url: reader.result as string,
              });
            reader.readAsDataURL(file);
          })
      );

      Promise.all(readers).then((parts) =>
        setPendingFiles((prev) => [...prev, ...parts])
      );
    },
    []
  );

  const handleSend = useCallback(() => {
    const text = input.trim();
    if ((!text && pendingFiles.length === 0) || isStreaming) return;
    // Capture image URLs before clearing pendingFiles
    const imageUrls = pendingFiles
      .filter((f) => f.mediaType?.startsWith("image/"))
      .map((f) => f.url);
    if (imageUrls.length > 0) {
      setSentImageUrls((prev) => [...prev, ...imageUrls]);
    }
    setInput("");
    setPendingFiles([]);
    sendMessage({ text, files: pendingFiles.length > 0 ? pendingFiles : undefined });
  }, [input, pendingFiles, isStreaming, sendMessage]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
    },
    [handleSend]
  );

  const handleSendEmails = useCallback(async () => {
    if (!emailDraft) { toast.error("No email draft yet. Use the chat to create one first."); return; }
    if (selectedIds.length === 0) { toast.error("Select at least one recipient."); return; }
    const token = accessTokenRef.current;
    if (!token) { toast.error("Not authenticated."); return; }

    setIsSending(true);
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/send-mail-comms`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          apikey: SUPABASE_KEY,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          recipientUserIds: selectedIds,
          subject: emailDraft.subject,
          heading: emailDraft.heading,
          subheading: emailDraft.subheading,
          bodyHtml: emailDraft.bodyHtml,
          buttons: emailDraft.buttons,
          imageUrls: emailDraft.includeImages ? conversationImageUrls : [],
        }),
      });
      const json = await res.json();
      if (!res.ok || json.error) throw new Error(json.error ?? "Send failed");
      if (json.skipped) { toast.warning("SMTP is not configured — emails were not sent."); return; }
      const { sentCount, failedCount } = json;
      setHistoryKey((k) => k + 1);
      if (failedCount > 0) {
        toast.warning(`Sent ${sentCount} email${sentCount !== 1 ? "s" : ""}, ${failedCount} failed.`);
      } else {
        toast.success(`Email sent to ${sentCount} recipient${sentCount !== 1 ? "s" : ""}.`);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to send emails.");
    } finally {
      setIsSending(false);
    }
  }, [emailDraft, selectedIds, conversationImageUrls]);

  const visibleMessages = messages.filter((m) => {
    const parts = m.parts as { type: string }[];
    if (m.role === "user") return parts.some((p) => p.type === "text" || p.type === "file");
    if (m.role === "assistant") return parts.some((p) => p.type === "text");
    return false;
  });

  return (
    <div className="flex flex-1 flex-col overflow-hidden bg-background">
      <PageHeader
        title="Mail Comms"
        description="Compose and send branded emails to users"
        onBack={onBack}
      />

      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* ── Left: Recipients + Chat ── */}
        <div className="flex w-2/5 min-w-0 flex-col border-r border-border">
          {/* Recipients */}
          <div className="shrink-0 space-y-2.5 border-b border-border p-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-foreground">Recipients</p>
              {selectedIds.length > 0 && (
                <button
                  className="text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => setSelectedIds([])}
                >
                  Clear all
                </button>
              )}
            </div>
            <RecipientsPicker
              users={users}
              loading={usersLoading}
              selectedIds={selectedIds}
              onChange={setSelectedIds}
            />
            {selectedIds.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {selectedIds.slice(0, 5).map((id) => {
                  const user = users.find((u) => u.user_id === id);
                  if (!user) return null;
                  return (
                    <Badge key={id} variant="secondary" className="gap-1 pr-1.5 text-xs">
                      {getDisplayName(user)}
                      <button
                        className="ml-0.5 rounded-full hover:bg-muted-foreground/20"
                        onClick={() => setSelectedIds((ids) => ids.filter((x) => x !== id))}
                        aria-label={`Remove ${getDisplayName(user)}`}
                      >
                        ×
                      </button>
                    </Badge>
                  );
                })}
                {selectedIds.length > 5 && (
                  <Badge variant="outline" className="text-xs">+{selectedIds.length - 5} more</Badge>
                )}
              </div>
            )}
          </div>

          {/* Chat Messages */}
          <ScrollArea className="flex-1 min-h-0">
            <div className="flex flex-col gap-3 p-4">
              {visibleMessages.length === 0 && (
                <div className="flex flex-col items-center justify-center gap-2 py-10 text-center text-muted-foreground">
                  <p className="text-sm font-medium">Start composing</p>
                  <p className="text-xs max-w-50">
                    Describe your email — topic, tone, key points — and the AI will draft it.
                  </p>
                </div>
              )}
              {visibleMessages.map((msg) => (
                <ChatMessage key={msg.id} message={msg} />
              ))}
              {isStreaming && (
                <div className="flex justify-start">
                  <div className="rounded-2xl bg-muted px-3.5 py-2.5">
                    <Spinner className="size-4" />
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </ScrollArea>

          {/* Input */}
          <div className="shrink-0 border-t border-border p-3 space-y-2">
            {/* Pasted image thumbnails */}
            {pendingFiles.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {pendingFiles.map((file, i) => (
                  <div key={i} className="relative size-14 shrink-0 overflow-hidden rounded-md border border-border bg-muted">
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img src={file.url} alt={file.filename ?? "pasted"} className="size-full object-cover" />
                    <button
                      className="absolute right-0.5 top-0.5 flex size-4 items-center justify-center rounded-full bg-background/80 text-[10px] text-foreground hover:bg-background"
                      onClick={() => setPendingFiles((prev) => prev.filter((_, j) => j !== i))}
                      aria-label="Remove image"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              placeholder="Describe the email you want to send… (paste screenshots with ⌘V)"
              className="min-h-18 max-h-40 resize-none text-sm"
              disabled={isStreaming}
            />
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs text-muted-foreground">Shift+Enter for new line</p>
              <Button size="sm" onClick={handleSend} disabled={(!input.trim() && pendingFiles.length === 0) || isStreaming}>
                {isStreaming ? <Spinner className="size-3.5" /> : "Draft"}
              </Button>
            </div>
          </div>

          {/* Send Email Button */}
          <div className="shrink-0 border-t border-border p-3">
            <Button
              className="w-full gap-2"
              onClick={handleSendEmails}
              disabled={!emailDraft || selectedIds.length === 0 || isSending}
            >
              {isSending ? <Spinner className="size-4" /> : <SendIcon size={16} />}
              {isSending
                ? "Sending…"
                : selectedIds.length > 0
                ? `Send to ${selectedIds.length} recipient${selectedIds.length !== 1 ? "s" : ""}`
                : "Send Email"}
            </Button>
          </div>
        </div>

        {/* ── Right: Preview + History ── */}
        <Tabs defaultValue="preview" className="flex flex-1 min-w-0 flex-col overflow-hidden">
          <div className="shrink-0 border-b border-border px-4 py-2 flex items-center justify-between gap-4">
            <div className="min-w-0">
              <TabsList variant="line" className="h-8">
                <TabsTrigger value="preview" className="text-xs px-2">Preview</TabsTrigger>
                <TabsTrigger value="history" className="text-xs px-2">Sent History</TabsTrigger>
              </TabsList>
            </div>
            {emailDraft && (
              <p className="text-xs text-muted-foreground truncate">Subject: {emailDraft.subject}</p>
            )}
          </div>
          <TabsContent value="preview" className="flex flex-1 min-h-0 overflow-hidden">
            <EmailPreview draft={emailDraft} imageUrls={conversationImageUrls} />
          </TabsContent>
          <TabsContent value="history" className="flex flex-1 min-h-0 overflow-hidden">
            <SentHistory refreshKey={historyKey} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
